import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socket';
import { apiService } from '../services/api';
import { TopicCategory, getTopicsByMode } from '../builtInDictionaries';
import { MatchingMode } from '../utils/matchingLogic';
import '../styles/TopicSelection.css';

interface Dictionary {
  id?: number;
  name: string;
  data: Record<string, string> | any; // Support all dictionary formats
  type?: 'builtin' | 'saved';
  matching_mode?: 'one-to-one' | 'category' | 'sort';
}

interface Room {
  id: number;
  roomCode: string;
  hostUserId: number;
  maxPlayers: number;
  matching_mode?: 'one-to-one' | 'category' | 'sort';
}

interface Participant {
  user_id: number;
  username: string;
  ready: boolean;
  is_host: boolean;
}

interface TopicProposal {
  proposerId: number;
  proposerName: string;
  topicName: string;
  dictionaryData: Record<string, string> | any; // Support both one-to-one and category dictionaries
  approvals: Set<number>;
  rejections: Set<number>;
  totalPlayers: number;
}

interface TopicSelectionProps {
  room: Room;
  participants: Participant[];
  topicCategories: TopicCategory[];
  onTopicApproved: (dictionary: Dictionary, newHostId: number) => void;
  onBack: () => void;
  onParticipantsUpdate?: (participants: Participant[]) => void;
}

const TopicSelection: React.FC<TopicSelectionProps> = ({
  room,
  participants: participantsProp,
  topicCategories,
  onTopicApproved,
  onBack,
  onParticipantsUpdate
}) => {
  const { user } = useAuth();
  const [activeProposal, setActiveProposal] = useState<TopicProposal | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [myProposal, setMyProposal] = useState(false);

  // Local participants state that stays synchronized via socket events
  const [participants, setParticipants] = useState<Participant[]>(participantsProp);

  // Waiting room state
  const [playersInWaitingRoom, setPlayersInWaitingRoom] = useState<Set<number>>(new Set());
  const [showWaitingOverlay, setShowWaitingOverlay] = useState(true);
  const [waitingTime, setWaitingTime] = useState(0);
  const [userStages, setUserStages] = useState<Map<number, { userId: number; username: string; stage: string }>>(new Map());

  // Saved dictionaries state
  const [savedDictionaries, setSavedDictionaries] = useState<Dictionary[]>([]);

  // Player left notification
  const [playerLeftNotification, setPlayerLeftNotification] = useState<string | null>(null);

  // Update local participants when prop changes
  useEffect(() => {
    setParticipants(participantsProp);
  }, [participantsProp]);

  // Load saved dictionaries on mount
  useEffect(() => {
    if (!user) return;

    apiService.getDictionaries()
      .then((response: any) => {
        const dicts = Array.isArray(response) ? response : (response.dictionaries || []);
        const formattedDicts: Dictionary[] = dicts.map((dict: any) => ({
          id: dict.id,
          name: dict.name,
          data: dict.data || {},
          type: 'saved' as const,
          matching_mode: dict.matching_mode || 'one-to-one'
        }));
        // Filter to only show dictionaries matching the room's game type
        const roomMode = (room.matching_mode || 'one-to-one') as MatchingMode;
        setSavedDictionaries(formattedDicts.filter(d => d.matching_mode === roomMode));
      })
      .catch(err => {
        console.error('Failed to load saved dictionaries:', err);
      });
  }, [user, room.matching_mode]);

  // Handle player entering waiting room
  const handlePlayerEnteredWaitingRoom = useCallback((data: { userId: number; username: string; playersInWaitingRoom: number[] }) => {
    console.log('🚪 Player entered waiting room:', data);
    setPlayersInWaitingRoom(new Set(data.playersInWaitingRoom));
  }, []);

  // Handle player leaving waiting room
  const handlePlayerLeftWaitingRoom = useCallback((data: { userId: number; playersInWaitingRoom: number[] }) => {
    console.log('🚪 Player left waiting room:', data);
    setPlayersInWaitingRoom(new Set(data.playersInWaitingRoom));
  }, []);

  // Handle player joining the room
  const handlePlayerJoined = useCallback((data: { participants: Participant[] }) => {
    console.log('👤 Player joined room:', data);
    setParticipants(data.participants);
    if (onParticipantsUpdate) {
      onParticipantsUpdate(data.participants);
    }
  }, [onParticipantsUpdate]);

  // Handle player leaving the room
  const handlePlayerLeft = useCallback((data: { userId: number; participants: Participant[] }) => {
    console.log('👋 Player left room:', data);

    // Find the username of the player who left
    const leftPlayer = participants.find(p => p.user_id === data.userId);
    if (leftPlayer) {
      setPlayerLeftNotification(`${leftPlayer.username} left the room`);
      setTimeout(() => setPlayerLeftNotification(null), 5000);
    }

    setParticipants(data.participants);
    if (onParticipantsUpdate) {
      onParticipantsUpdate(data.participants);
    }

    // Remove from waiting room if they were in it
    setPlayersInWaitingRoom(prev => {
      const newSet = new Set(prev);
      newSet.delete(data.userId);
      return newSet;
    });
  }, [onParticipantsUpdate, participants]);

  // Handle user stages update
  const handleUserStagesUpdate = useCallback((data: { stages: Array<{ userId: number; username: string; stage: string }> }) => {
    console.log('📍 User stages update:', data);
    const stagesMap = new Map(data.stages.map(s => [s.userId, s]));
    setUserStages(stagesMap);
  }, []);

  // Handle player ready status changed
  const handlePlayerReadyChanged = useCallback((data: { participants: Participant[] }) => {
    console.log('✅ Player ready changed in waiting room:', data);
    // Update local participants state immediately
    setParticipants(data.participants);
    // Also notify parent component
    if (onParticipantsUpdate) {
      onParticipantsUpdate(data.participants);
    }
  }, [onParticipantsUpdate]);

  // Handle host proceeding to topic selection
  const handleHostProceed = useCallback(() => {
    console.log('🚀 Host proceeding to topic selection');
    setShowWaitingOverlay(false);
  }, []);

  // Waiting room timer
  useEffect(() => {
    if (showWaitingOverlay) {
      const timer = setInterval(() => {
        setWaitingTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showWaitingOverlay]);

  // Check ONLY if all players are physically in waiting room (auto-proceed)
  // Don't auto-hide if players are just "ready" in lobby - host must click continue
  useEffect(() => {
    if (playersInWaitingRoom.size >= participants.length && participants.length >= 2) {
      console.log('✅ All players in waiting room, hiding overlay');
      setShowWaitingOverlay(false);
    } else if (participants.length < 2 && playersInWaitingRoom.size > 0) {
      console.log('⚠️ Not enough players');
      // Will show "not enough players" in overlay
    }
  }, [playersInWaitingRoom, participants]);

  // Handle new topic proposal
  const handleTopicProposed = useCallback((data: {
    proposerId: number;
    proposerName: string;
    topicName: string;
    dictionaryData: Record<string, string>;
    totalPlayers: number;
  }) => {
    console.log('📢 Topic proposed:', data);
    setActiveProposal({
      ...data,
      approvals: new Set([data.proposerId]), // Proposer auto-approves
      rejections: new Set()
    });
    setCountdown(15);
    setHasVoted(data.proposerId === user?.id);
    setMyProposal(data.proposerId === user?.id);
  }, [user]);

  // Handle approval
  const handleTopicApproval = useCallback((data: { userId: number }) => {
    setActiveProposal(prev => {
      if (!prev) return null;
      const newApprovals = new Set(prev.approvals);
      newApprovals.add(data.userId);
      return { ...prev, approvals: newApprovals };
    });
  }, []);

  // Handle rejection
  const handleTopicRejection = useCallback((data: { userId: number, username: string }) => {
    console.log('❌ Topic rejected by:', data.username);
    setActiveProposal(prev => {
      if (!prev) return null;
      const newRejections = new Set(prev.rejections);
      newRejections.add(data.userId);
      return { ...prev, rejections: newRejections };
    });
  }, []);

  // Handle final approval (everyone accepted)
  const handleTopicApproved = useCallback((data: {
    newHostId: number;
    topicName: string;
    dictionaryData: Record<string, string>;
  }) => {
    console.log('✅ Topic approved! New host:', data.newHostId);

    if (user?.id === data.newHostId) {
      // I'm the new host - show success message then transition
      setTimeout(() => {
        onTopicApproved({
          name: data.topicName,
          data: data.dictionaryData
        }, data.newHostId);
      }, 2000);
    } else {
      // Not the host - just transition
      onTopicApproved({
        name: data.topicName,
        data: data.dictionaryData
      }, data.newHostId);
    }

    setActiveProposal(null);
  }, [user, onTopicApproved]);

  // Handle proposal cancellation
  const handleProposalCancelled = useCallback(() => {
    console.log('🚫 Proposal cancelled');
    setActiveProposal(null);
    setCountdown(null);
    setHasVoted(false);
    setMyProposal(false);
  }, []);

  // Setup socket listeners AND emit enter_waiting_room
  useEffect(() => {
    if (!user) return;

    // Register listeners FIRST
    socketService.on('topic_proposed', handleTopicProposed);
    socketService.on('topic_approval', handleTopicApproval);
    socketService.on('topic_rejection', handleTopicRejection);
    socketService.on('topic_approved', handleTopicApproved);
    socketService.on('proposal_cancelled', handleProposalCancelled);
    socketService.on('player_entered_waiting_room', handlePlayerEnteredWaitingRoom);
    socketService.on('player_left_waiting_room', handlePlayerLeftWaitingRoom);
    socketService.on('player_joined', handlePlayerJoined);
    socketService.on('player_left', handlePlayerLeft);
    socketService.on('player_ready_changed', handlePlayerReadyChanged);
    socketService.on('user_stages_update', handleUserStagesUpdate);
    socketService.on('proceed_to_topics', handleHostProceed);

    // THEN emit enter_waiting_room (after listeners are set up)
    console.log('🚪 Entering waiting room for round 2+');
    socketService.getSocket()?.emit('enter_waiting_room', {
      roomId: room.id,
      userId: user.id,
      username: user.username
    });

    // Update stage to 'waiting_room'
    socketService.getSocket()?.emit('update_user_stage', {
      roomId: room.id,
      userId: user.id,
      username: user.username,
      stage: 'waiting_room'
    });

    return () => {
      // Cleanup: leave waiting room and remove listeners
      socketService.getSocket()?.emit('leave_waiting_room', {
        roomId: room.id,
        userId: user.id
      });

      socketService.off('topic_proposed', handleTopicProposed);
      socketService.off('topic_approval', handleTopicApproval);
      socketService.off('topic_rejection', handleTopicRejection);
      socketService.off('topic_approved', handleTopicApproved);
      socketService.off('proposal_cancelled', handleProposalCancelled);
      socketService.off('player_entered_waiting_room', handlePlayerEnteredWaitingRoom);
      socketService.off('player_left_waiting_room', handlePlayerLeftWaitingRoom);
      socketService.off('player_joined', handlePlayerJoined);
      socketService.off('player_left', handlePlayerLeft);
      socketService.off('player_ready_changed', handlePlayerReadyChanged);
      socketService.off('user_stages_update', handleUserStagesUpdate);
      socketService.off('proceed_to_topics', handleHostProceed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, room.id]); // Only re-run if user or room changes, NOT when handlers change

  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Auto-approve on timeout if haven't voted
          if (activeProposal && !hasVoted && user) {
            socketService.getSocket()?.emit('approve_topic', {
              roomId: room.id,
              userId: user.id
            });
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, activeProposal, hasVoted, user, room.id]);

  const handleSelectTopic = (topicName: string, dictionaryData: Record<string, string> | any) => {
    if (!user || activeProposal) return;

    console.log('📤 Proposing topic:', topicName);
    socketService.getSocket()?.emit('propose_topic', {
      roomId: room.id,
      proposerId: user.id,
      proposerName: user.username,
      topicName,
      dictionaryData,
      totalPlayers: participants.length
    });
  };

  const handleApprove = () => {
    if (!user || hasVoted) return;

    socketService.getSocket()?.emit('approve_topic', {
      roomId: room.id,
      userId: user.id
    });
    setHasVoted(true);
  };

  const handleReject = () => {
    if (!user || hasVoted) return;

    socketService.getSocket()?.emit('reject_topic', {
      roomId: room.id,
      userId: user.id,
      username: user.username
    });
    setHasVoted(true);
  };

  const getMaxPairs = (dict: Dictionary) => {
    const keys = Object.keys(dict.data);
    const pairs = new Set<string>();

    for (const key of keys) {
      const value = dict.data[key];
      const pairId = [key, value].sort().join('|');
      pairs.add(pairId);
    }

    return pairs.size;
  };

  // Show proposal popup
  if (activeProposal) {
    const isRejected = activeProposal.rejections.size > 0;
    const isApproved = activeProposal.approvals.size === activeProposal.totalPlayers;
    const rejecter = isRejected ? participants.find(p => activeProposal.rejections.has(p.user_id)) : null;

    return (
      <div className="topic-selection-container">
        <div className="topic-proposal-overlay">
          <div className="topic-proposal-popup">
            {myProposal ? (
              // Proposer view
              <>
                <h2>⏳ Waiting for Approval</h2>
                <p className="proposal-topic">Topic: <strong>{activeProposal.topicName}</strong></p>
                <div className="approval-status">
                  <p>Approved: {activeProposal.approvals.size} / {activeProposal.totalPlayers}</p>
                  {countdown !== null && <p className="countdown">Time remaining: {countdown}s</p>}
                </div>

                {isRejected && (
                  <div className="rejection-notice">
                    <p className="rejected">❌ {rejecter?.username} declined the topic</p>
                    <p>Please select a different topic</p>
                  </div>
                )}

                {isApproved && (
                  <div className="approval-notice">
                    <p className="approved">✅ Topic Approved!</p>
                    <p>You are now the host</p>
                  </div>
                )}
              </>
            ) : (
              // Voter view
              <>
                <h2>Topic Proposal</h2>
                <p className="proposal-info">
                  <strong>{activeProposal.proposerName}</strong> selected topic:
                </p>
                <p className="proposal-topic">{activeProposal.topicName}</p>

                {countdown !== null && <p className="countdown">Time remaining: {countdown}s</p>}

                {isRejected ? (
                  <div className="rejection-notice">
                    <p className="rejected">❌ {rejecter?.username} declined the topic</p>
                    <p>Waiting for new selection...</p>
                  </div>
                ) : hasVoted ? (
                  <div className="voted-notice">
                    <p>✓ Your vote has been recorded</p>
                    <p>Waiting for others... ({activeProposal.approvals.size}/{activeProposal.totalPlayers})</p>
                  </div>
                ) : (
                  <div className="vote-buttons">
                    <button className="accept-button" onClick={handleApprove}>
                      ✓ Accept
                    </button>
                    <button className="decline-button" onClick={handleReject}>
                      ✗ Decline
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show topic selection
  return (
    <div className="topic-selection-container">
      {/* Waiting Room Overlay */}
      {showWaitingOverlay && (
        <div className="waiting-room-overlay">
          <div className="waiting-room-popup">
            <h2>⏳ Waiting Room</h2>
            <p className="room-code-display">Room Code: <strong>{room.roomCode}</strong></p>
            <p className="waiting-timer">
              Time waiting: {Math.floor(waitingTime / 60)}:{(waitingTime % 60).toString().padStart(2, '0')}
            </p>

            {/* Player left notification */}
            {playerLeftNotification && (
              <div className="player-left-notification">
                ⚠️ {playerLeftNotification}
              </div>
            )}

            {participants.length < 2 ? (
              <div className="not-enough-players">
                <p>⚠️ Not enough players</p>
                <p>{playerLeftNotification ? `${playerLeftNotification.split(' ')[0]} left the room.` : 'Invite more players to continue'}</p>
                <p className="invite-instruction">Share room code: <strong>{room.roomCode}</strong></p>
              </div>
            ) : (
              <>
                <p className="waiting-instruction">Waiting for all players to be ready...</p>

                <div className="player-status-list">
                  {participants.map(participant => {
                    const isInWaitingRoom = playersInWaitingRoom.has(participant.user_id);
                    const userStage = userStages.get(participant.user_id);
                    // Only count as ready if actually in waiting room - not just ready in lobby
                    const isReady = isInWaitingRoom;
                    return (
                      <div key={participant.user_id} className={`player-status-item ${isReady ? 'in-room' : 'not-in-room'}`}>
                        <span className="player-name">{participant.username}</span>
                        <span className="status-indicator">
                          {(() => {
                            if (isInWaitingRoom) return '✓ In waiting room';
                            if (!userStage) return '⏳ Loading...';
                            if (userStage.stage === 'lobby') return participant.ready ? '✓ Ready in lobby' : '⏳ In lobby, not ready';
                            if (userStage.stage === 'results') return '📊 On results screen';
                            return '⏳ Loading...';
                          })()}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <p className="progress-info">
                  {participants.filter(p => {
                    const inWaitingRoom = playersInWaitingRoom.has(p.user_id);
                    const userStage = userStages.get(p.user_id);
                    // Count as ready if:
                    // 1. In waiting room (stage = 'waiting_room'), OR
                    // 2. Ready in lobby (stage = 'lobby' AND ready = true), OR
                    // 3. On results page and clicked continue (ready = true, not yet in waiting room)
                    const readyInLobby = userStage && userStage.stage === 'lobby' && p.ready;
                    const readyOnResults = userStage && userStage.stage === 'results' && p.ready;
                    return inWaitingRoom || readyInLobby || readyOnResults;
                  }).length} / {participants.length} players ready
                </p>

                {/* Host can continue when 2+ players are ready */}
                {participants.find(p => p.user_id === user?.id)?.is_host &&
                 participants.filter(p => {
                   const inWaitingRoom = playersInWaitingRoom.has(p.user_id);
                   const userStage = userStages.get(p.user_id);
                   // Count as ready if:
                   // 1. In waiting room (stage = 'waiting_room'), OR
                   // 2. Ready in lobby (stage = 'lobby' AND ready = true), OR
                   // 3. On results page and clicked continue (ready = true, not yet in waiting room)
                   const readyInLobby = userStage && userStage.stage === 'lobby' && p.ready;
                   const readyOnResults = userStage && userStage.stage === 'results' && p.ready;
                   return inWaitingRoom || readyInLobby || readyOnResults;
                 }).length >= 2 && (
                  <button
                    className="continue-button"
                    onClick={() => {
                      // Broadcast to all players to proceed to topic selection
                      socketService.getSocket()?.emit('host_proceed_to_topics', {
                        roomId: room.id,
                        hostId: user?.id
                      });
                      setShowWaitingOverlay(false);
                    }}
                  >
                    Continue to Topic Selection →
                  </button>
                )}
              </>
            )}

            <button className="quit-waiting-button" onClick={() => {
              // Leave waiting room and go back
              if (user) {
                socketService.getSocket()?.emit('leave_waiting_room', {
                  roomId: room.id,
                  userId: user.id
                });
              }
              onBack();
            }}>
              Quit Waiting Room
            </button>
          </div>
        </div>
      )}

      <div className={`topic-selection-content ${showWaitingOverlay ? 'blurred' : ''}`}>
        <div className="topic-selection-header">
          <h2>Select Topic for Next Round</h2>
          <p className="room-info">Room: <strong>{room.roomCode}</strong> • Players: {participants.length}/{room.maxPlayers}</p>
          <p className="instruction">Anyone can propose a topic. All players must agree to proceed.</p>
        </div>

        <div className="topics-grid">
        {getTopicsByMode((room.matching_mode || 'one-to-one') as MatchingMode).map((category) => (
          <div key={category.id} className="category-section">
            <div className="category-header">
              <span className="category-icon">{category.icon}</span>
              <h3 className="category-name">{category.name}</h3>
            </div>
            <div className="topics-list">
              {category.games.map((game) => (
                <button
                  key={game.id}
                  className="topic-card"
                  onClick={() => handleSelectTopic(game.name, game.data)}
                  disabled={!!activeProposal}
                >
                  <div className="topic-name">{game.name}</div>
                  <div className="topic-pairs">{getMaxPairs({ name: game.name, data: game.data })} matches</div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Saved Dictionaries Section */}
        {savedDictionaries.length > 0 && (
          <div className="category-section">
            <div className="category-header">
              <span className="category-icon">💾</span>
              <h3 className="category-name">Saved Games</h3>
            </div>
            <div className="topics-list">
              {savedDictionaries.map((dict) => (
                <button
                  key={dict.id}
                  className="topic-card"
                  onClick={() => handleSelectTopic(dict.name, dict.data)}
                  disabled={!!activeProposal}
                >
                  <div className="topic-name">{dict.name}</div>
                  <div className="topic-pairs">{getMaxPairs(dict)} matches</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

        <div className="topic-selection-footer">
          <button className="back-button" onClick={() => {
            if (user) {
              // Properly leave the room by emitting leave_room event
              socketService.getSocket()?.emit('leave_room', {
                roomId: room.id,
                userId: user.id
              });
              // Also leave waiting room if in it
              socketService.getSocket()?.emit('leave_waiting_room', {
                roomId: room.id,
                userId: user.id
              });
            }
            onBack();
          }}>
            ← Leave Room
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicSelection;
